// AppContext.js
import { createContext, useContext, useEffect, useRef, useState } from "react";
import useFetchLinkedServices from "../hooks/useFetchLinkedServices";
// Removed: import useFetchConnectionKeysAll from "../hooks/useFetchConnectionKeysAll"; // Not used in this context
import useFetchResourcesByIdMultipleIds from "../hooks/useFetchResourcesByMultipleIds";
import useFetchAllResources from "../hooks/useFetchAllResources";
import useFetchResourceByResourceId from "../hooks/useFetchResourceByResourceId";
import AceEditor from "react-ace";
import useFetchRuleById from "../hooks/useFetchRuleById";
import useFetchResourceColumnsByMultipleIds from "../hooks/useFetchResourceColumnsByMultipleIds";
import { StructuredColumnData } from "../services/utils/mergeResourceColumns";

// import { IResourceData } from "../types/resource";

// Interface for the new variable management state
interface ValidateResourceVariableState {
  allVariables: { [key: string]: any };
  usedVariableNames: string[];
  isEditing: boolean;
  editingVariables: { [key: string]: any };
  originalVariables: { [key: string]: any };
  showVariables: "used" | "unused";
}

interface RuleResourceContextProps {
  availColumns: any;
  setAvailColumns: React.Dispatch<React.SetStateAction<any>>;
  availColumnsWithResourceDetail: { queryType: string; data: any[] } | null;
  setAvailColumnsWithResourceDetail: React.Dispatch<
    React.SetStateAction<{ queryType: string; data: any[] } | null>
  >;

  globalVariables: any;
  setGlobalVariables: React.Dispatch<React.SetStateAction<any>>;
  tempGlobalVariables: any;
  setTempGlobalVariables: React.Dispatch<React.SetStateAction<any>>;
  temporaryQueryValue: any;
  setTemporaryQueryValue: React.Dispatch<React.SetStateAction<any>>;
  queryBuilderTempValue: any;
  setQueryBuilderTempValue: React.Dispatch<React.SetStateAction<any>>;
  type: any;
  setType: React.Dispatch<React.SetStateAction<any>>;
  linkedServicesData: any;
  setLinkedServicesData: React.Dispatch<React.SetStateAction<any>>;
  fetchedConnectionKeys: any;
  setFetchedConnectionKeys: React.Dispatch<React.SetStateAction<any>>;
  fetchedAllConnectionKeys: any;
  setFetchedAllConnectionKeys: React.Dispatch<React.SetStateAction<any>>;
  isLoading: any;
  setIsLoading: any;
  resourceData: any;
  setResourceData: React.Dispatch<React.SetStateAction<any>>;
  resourceIds: any;
  setResourceIds: any;
  allResourcesData: any;
  setAllResourcesData: React.Dispatch<React.SetStateAction<any>>;
  resourceDetailData: any;
  setResourceDetailData: React.Dispatch<React.SetStateAction<any>>;
  currentResourceId: string | number;
  setCurrentResourceId: React.Dispatch<React.SetStateAction<string | number>>;
  editorRef: any;
  currentRuleId: string;
  setCurrentRuleId: React.Dispatch<React.SetStateAction<string | number>>;
  ruleData: any;
  setRuleData: React.Dispatch<React.SetStateAction<any>>;
  resourceColIds: any;
  setResourceColIds: React.Dispatch<React.SetStateAction<any>>;
  resourceColumnsData: any;
  setResourceColumnsData: React.Dispatch<React.SetStateAction<any>>;
  showAggregatedColumns: any;
  setShowAggregatedColumns: React.Dispatch<React.SetStateAction<any>>;
  aggregatedAvailColumns: any;
  setAggregatedAvailColumns: React.Dispatch<React.SetStateAction<any>>;
  setIsResourceEdit: React.Dispatch<React.SetStateAction<any>>;
  isResourceEdit: any;
  viewInlineVariables: any;
  setViewInlineVariables: React.Dispatch<React.SetStateAction<any>>;
  fileProcessingData: any;
  setFileProcessingData: React.Dispatch<React.SetStateAction<any>>;
  // New variable management state and functions
  validateResourceVariable: ValidateResourceVariableState;
  setValidateResourceVariable: React.Dispatch<
    React.SetStateAction<ValidateResourceVariableState>
  >;
  initializeVariables: (allVars: any, usedVars: string[]) => void;
  startEditingVariables: () => void;
  saveVariableChanges: () => void;
  cancelVariableChanges: () => void;
  updateEditingVariable: (key: string, value: any) => void;
  updateUsedVariableNames: (usedVars: string[]) => void;
  getFilteredVariables: () => { [key: string]: any };
  hasAnyVariables: () => boolean;
  hasVariableChanges: () => boolean;
  toggleVariableView: (view: "used" | "unused") => void;
  updateVariableUsageFromTempGlobalVariables: () => void;
  collectUsedVariables: (
    resource: any,
    collectedVars?: Set<string>,
  ) => Set<string>;
  updateViewInlineVariables: (resource_id: number) => void;
  seletedResourceId: any;
  setSeletedResourceId: React.Dispatch<React.SetStateAction<any>>;
  hasChanges: boolean;
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
  // Persisted SQL editor expansion state, keyed per editor/sub-query instance
  // so it survives remounts when navigating between sub-queries.
  editorExpandedMap: Record<string, boolean>;
  setEditorExpanded: (key: string, expanded: boolean) => void;
  // Persisted SQL editor cursor position (row/column), keyed per editor/sub-query
  // instance so it survives remounts when navigating between sub-queries.
  editorCursorMap: Record<string, { row: number; column: number }>;
  setEditorCursor: (
    key: string,
    position: { row: number; column: number },
  ) => void;
}

const RuleResourceContext = createContext<RuleResourceContextProps | undefined>(
  undefined,
);

const RuleResourceProvider = ({ children }: any) => {
  const [availColumns, setAvailColumns] = useState<
    string[] | StructuredColumnData
  >([]);
  const [availColumnsWithResourceDetail, setAvailColumnsWithResourceDetail] =
    useState<{ queryType: string; data: any[] } | null>(null);

  const [globalVariables, setGlobalVariables] = useState<any>({});
  const [tempGlobalVariables, setTempGlobalVariables] = useState<any>({});
  const [resourceIds, setResourceIds] = useState<any[]>([]);
  const [resourceColIds, setResourceColIds] = useState<any[]>([]);
  const [queryBuilderTempValue, setQueryBuilderTempValue] = useState("");
  const [temporaryQueryValue, setTemporaryQueryValue] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentResourceId, setCurrentResourceId] = useState<string | number>(
    0,
  );
  const [type, setType] = useState<any>("");
  const [currentRuleId, setCurrentRuleId] = useState<any>(0);
  const editorRef = useRef<AceEditor | null>(null);

  const [isResourceEdit, setIsResourceEdit] = useState<string>("");
  const [viewInlineVariables, setViewInlineVariables] = useState<any>({
    rule_variables: {
      filterRule: null,
      adhoc_queries: null,
    },
    resource_variables: [],
    comparison_research_query_variables: [],
  });

  const [linkedServicesData, setLinkedServicesData] = useState<any>([]);
  const [fetchedConnectionKeys, setFetchedConnectionKeys] = useState<any>([]);
  const [fetchedAllConnectionKeys, setFetchedAllConnectionKeys] = useState<any>(
    [],
  );
  const [fileProcessingData, setFileProcessingData] = useState<any>([]);
  const [resourceData, setResourceData] = useState<any>([]);
  const [allResourcesData, setAllResourcesData] = useState<any>([]);
  const [resourceDetailData, setResourceDetailData] = useState<any>({});
  const [ruleData, setRuleData] = useState<any>({});
  const [resourceColumnsData, setResourceColumnsData] = useState<any>([]);
  const [showAggregatedColumns, setShowAggregatedColumns] =
    useState<any>(false);
  const [aggregatedAvailColumns, setAggregatedAvailColumns] = useState<any>([]);
  const [seletedResourceId, setSeletedResourceId] = useState<any>(null);

  // New variable management state
  const [validateResourceVariable, setValidateResourceVariable] =
    useState<ValidateResourceVariableState>({
      allVariables: {},
      usedVariableNames: [],
      isEditing: false,
      editingVariables: {},
      originalVariables: {},
      showVariables: "used",
    });

  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // Persisted SQL editor expansion state, keyed per editor/sub-query instance.
  const [editorExpandedMap, setEditorExpandedMap] = useState<
    Record<string, boolean>
  >({});
  const setEditorExpanded = (key: string, expanded: boolean) => {
    setEditorExpandedMap((prev) => ({ ...prev, [key]: expanded }));
  };

  // Persisted SQL editor cursor position, keyed per editor/sub-query instance.
  const [editorCursorMap, setEditorCursorMap] = useState<
    Record<string, { row: number; column: number }>
  >({});
  const setEditorCursor = (
    key: string,
    position: { row: number; column: number },
  ) => {
    setEditorCursorMap((prev) => ({ ...prev, [key]: position }));
  };

  // Variable management helper functions
  const initializeVariables = (allVars: any, usedVars: string[]) => {
    setValidateResourceVariable((prev) => ({
      ...prev,
      allVariables: allVars || {},
      usedVariableNames: usedVars || [],
      originalVariables: JSON.parse(JSON.stringify(allVars || {})),
    }));
  };

  const startEditingVariables = () => {
    setValidateResourceVariable((prev) => ({
      ...prev,
      isEditing: true,
      editingVariables: JSON.parse(JSON.stringify(prev.allVariables)),
    }));
  };

  const updateVariableUsageFromTempGlobalVariables = () => {
    // Detect newly used variables from QueryBuilder
    if (tempGlobalVariables && Object.keys(tempGlobalVariables).length > 0) {
      const tempVariableKeys = Object.keys(tempGlobalVariables);
      const currentUsedVars = validateResourceVariable.usedVariableNames;

      // Find variables that are in tempGlobalVariables but not in usedVariableNames
      const newlyUsedVars = tempVariableKeys.filter(
        (varKey) => !currentUsedVars.includes(varKey.toLowerCase()),
      );

      // If there are newly used variables, add them to the used list (one-way movement)
      if (newlyUsedVars.length > 0) {
        const updatedUsedVars = [
          ...currentUsedVars,
          ...newlyUsedVars.map((v) => v.toLowerCase()),
        ];

        // Update the used variables list (only adding, never removing)
        setValidateResourceVariable((prev) => ({
          ...prev,
          usedVariableNames: updatedUsedVars,
        }));
      }
    }
  };

  useEffect(() => {
    if (
      validateResourceVariable.allVariables &&
      Object.keys(validateResourceVariable.allVariables).length > 0
    ) {
      setGlobalVariables(validateResourceVariable.allVariables || {});
    }
  }, [validateResourceVariable.allVariables]);

  const saveVariableChanges = () => {
    setValidateResourceVariable((prev) => ({
      ...prev,
      isEditing: false,
      allVariables: { ...prev.editingVariables },
      originalVariables: JSON.parse(JSON.stringify(prev.editingVariables)),
      editingVariables: {},
    }));

    // Update global variables for other components
    setGlobalVariables(validateResourceVariable.editingVariables);
  };

  const cancelVariableChanges = () => {
    setValidateResourceVariable((prev) => ({
      ...prev,
      isEditing: false,
      editingVariables: {},
    }));
  };

  const updateEditingVariable = (key: string, value: any) => {
    setValidateResourceVariable((prev) => ({
      ...prev,
      editingVariables: {
        ...prev.editingVariables,
        [key]: value,
      },
    }));
  };

  const updateUsedVariableNames = (usedVars: string[]) => {
    setValidateResourceVariable((prev) => ({
      ...prev,
      usedVariableNames: usedVars || [],
    }));
  };

  const getFilteredVariables = () => {
    const {
      allVariables,
      editingVariables,
      usedVariableNames,
      showVariables,
      isEditing,
    } = validateResourceVariable;
    const varsToFilter = isEditing ? editingVariables : allVariables;

    if (!varsToFilter || Object.keys(varsToFilter).length === 0) return {};

    return Object.fromEntries(
      Object.entries(varsToFilter).filter(([key]) => {
        const isUsed = usedVariableNames.includes(key);
        return showVariables === "used" ? isUsed : !isUsed;
      }),
    );
  };

  const hasAnyVariables = () => {
    const { allVariables, usedVariableNames, showVariables } =
      validateResourceVariable;

    if (!allVariables || Object.keys(allVariables).length === 0) return false;

    const hasUsedVariables = Object.keys(allVariables).some((key) =>
      usedVariableNames.includes(key),
    );

    const hasUnusedVariables = Object.keys(allVariables).some(
      (key) => !usedVariableNames.includes(key),
    );

    return showVariables === "used" ? hasUsedVariables : hasUnusedVariables;
  };

  const hasVariableChanges = () => {
    const { editingVariables, originalVariables, isEditing } =
      validateResourceVariable;

    if (!isEditing) return false;

    return (
      JSON.stringify(editingVariables) !== JSON.stringify(originalVariables)
    );
  };

  const toggleVariableView = (view: "used" | "unused") => {
    setValidateResourceVariable((prev) => ({
      ...prev,
      showVariables: view,
    }));
  };

  const collectUsedVariables = (
    resource: any,
    collectedVars = new Set<string>(),
  ) => {
    if (!resource) return collectedVars;

    // Add variables from resource_variables
    if (Array.isArray(resource.resource_variables)) {
      resource.resource_variables.forEach((v: string) => collectedVars.add(v));
    }

    // Add variables from resource_col_detail_variables
    if (Array.isArray(resource.resource_col_detail_variables)) {
      resource.resource_col_detail_variables.forEach((v: string) =>
        collectedVars.add(v),
      );
    }

    // Recurse into base_resource
    if (resource.base_resource) {
      collectUsedVariables(resource.base_resource, collectedVars);
    }

    // Recurse into additional_resources
    if (Array.isArray(resource.additional_resources)) {
      resource.additional_resources.forEach((res: any) => {
        collectUsedVariables(res, collectedVars);
      });
    }

    return collectedVars;
  };

  const updateViewInlineVariables = (resource_id: number) => {
    if (!resource_id) return;
    if (tempGlobalVariables && Object.keys(tempGlobalVariables).length > 0) {
      const tempVariableKeys = Object.keys(tempGlobalVariables);
      const currentUsedVars = viewInlineVariables?.used_variables?.find(
        (r: any) => r?.resource_id === resource_id,
      )?.merged_used_variables;
      if (!currentUsedVars) return;

      // Find variables that are in tempGlobalVariables but not in usedVariableNames
      const newlyUsedVars = tempVariableKeys.filter(
        (varKey) => !currentUsedVars.includes(varKey.toLowerCase()),
      );

      // If there are newly used variables, add them to the used list (one-way movement)
      if (newlyUsedVars.length > 0) {
        const updatedUsedVars = [
          ...currentUsedVars,
          ...newlyUsedVars.map((v) => v.toLowerCase()),
        ];

        // Update the used variables list (only adding, never removing)
        setViewInlineVariables((prev: any) => ({
          ...prev,
          used_variables: prev?.used_variables?.map((item: any) =>
            item?.resource_id === resource_id
              ? { ...item, merged_used_variables: updatedUsedVars }
              : item,
          ),
        }));
      }
    }
  };

  const ruleResourceContextValue = {
    availColumns,
    setAvailColumns,
    availColumnsWithResourceDetail,
    setAvailColumnsWithResourceDetail,
    globalVariables,
    setGlobalVariables,
    tempGlobalVariables,
    setTempGlobalVariables,
    queryBuilderTempValue,
    setQueryBuilderTempValue,
    temporaryQueryValue,
    setTemporaryQueryValue,
    type,
    setType,
    linkedServicesData,
    setLinkedServicesData,
    fetchedConnectionKeys,
    setFetchedConnectionKeys,
    fetchedAllConnectionKeys,
    setFetchedAllConnectionKeys,
    isLoading,
    setIsLoading,
    resourceData,
    setResourceData,
    resourceIds,
    setResourceIds,
    allResourcesData,
    setAllResourcesData,
    resourceDetailData,
    setResourceDetailData,
    currentResourceId,
    setCurrentResourceId,
    editorRef,
    currentRuleId,
    setCurrentRuleId,
    ruleData,
    setRuleData,
    resourceColumnsData,
    setResourceColumnsData,
    showAggregatedColumns,
    setShowAggregatedColumns,
    aggregatedAvailColumns,
    setAggregatedAvailColumns,
    resourceColIds,
    setResourceColIds,
    isResourceEdit,
    setIsResourceEdit,
    setViewInlineVariables,
    viewInlineVariables,
    fileProcessingData,
    setFileProcessingData,
    // New variable management
    validateResourceVariable,
    setValidateResourceVariable,
    initializeVariables,
    startEditingVariables,
    saveVariableChanges,
    cancelVariableChanges,
    updateEditingVariable,
    updateUsedVariableNames,
    getFilteredVariables,
    hasAnyVariables,
    hasVariableChanges,
    toggleVariableView,
    updateVariableUsageFromTempGlobalVariables,
    collectUsedVariables,
    updateViewInlineVariables,
    seletedResourceId,
    setSeletedResourceId,
    hasChanges,
    setHasChanges,
    editorExpandedMap,
    setEditorExpanded,
    editorCursorMap,
    setEditorCursor,
  };

  return (
    <RuleResourceContext.Provider value={ruleResourceContextValue}>
      {children}
    </RuleResourceContext.Provider>
  );
};

const useRuleResourceContext = () => {
  const context = useContext(RuleResourceContext);
  if (!context) {
    throw new Error(
      "useRuleResourceContext must be used within a RuleResourceProvider",
    );
  }
  return context;
};

export { RuleResourceProvider, useRuleResourceContext };
